import React, { useEffect, useState } from 'react';
import { configApi, TierLimit, UpdateTierLimitPayload } from '../../../api/config';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { useAuth } from '../../../hooks/useAuth';
import { useToast } from '../../../hooks/useToast';

/**
 * What the server uses for a tier whose Ibra chat allowance was never set.
 * Characters a day, what is asked and what Ibra answers; the app shows it
 * as a share of the day.
 */
const CHAT_DEFAULTS: Record<string, number> = {
  tier_0: 2500,
  tier_1: 10000,
  tier_2: 25000,
  tier_3: 50000,
};

/** What the server uses for a tier whose voice allowance was never set. */
const VOICE_DEFAULTS: Record<string, number> = {
  tier_0: 1000,
  tier_1: 2000,
  tier_2: 3000,
  tier_3: 4000,
};

const formatNaira = (n: number | null | undefined) =>
  `₦${Number(n || 0).toLocaleString('en-NG')}`;
const formatUsd = (n: number | null | undefined) =>
  `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const tierLabel = (tier: string) =>
  tier.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const parseNonNegative = (value: string) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

interface EditState {
  dailyTransactionLimit: string;
  monthlyTransactionLimit: string;
  balanceLimit: string;
  singleTransactionLimit: string;
  usdDailyTransactionLimit: string;
  usdMonthlyTransactionLimit: string;
  usdSingleTransactionLimit: string;
  ibraCharsPerDay: string;
  voiceCharsPerDay: string;
}

function LimitInputRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 54, fontSize: 11, color: 'var(--neutral-500)' }}>
        {label}
      </span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 86,
          height: 30,
          padding: '0 8px',
          fontSize: 12,
          fontFamily: 'var(--font-family)',
          color: 'var(--neutral-800)',
          background: 'var(--neutral-100)',
          border: '1px solid var(--blue-600)',
          borderRadius: 'var(--radius-md)',
          outline: 'none',
        }}
      />
    </label>
  );
}

export const TierLimits: React.FC = () => {
  const { admin } = useAuth();
  const [limits, setLimits] = useState<TierLimit[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTier, setEditingTier] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({
    dailyTransactionLimit: '',
    monthlyTransactionLimit: '',
    balanceLimit: '',
    singleTransactionLimit: '',
    usdDailyTransactionLimit: '',
    usdMonthlyTransactionLimit: '',
    usdSingleTransactionLimit: '',
    ibraCharsPerDay: '',
    voiceCharsPerDay: '',
  });
  const [pendingUpdate, setPendingUpdate] = useState<(UpdateTierLimitPayload & { tier: string }) | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canEdit = admin && (admin.role === 'super_admin' || admin.role === 'finance');
  const toast = useToast();

  const fetchLimits = async () => {
    try {
      setLoading(true);
      const data = await configApi.getTierLimits();
      setLimits(data);
    } catch {
      setError('Failed to load tier limits.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchLimits();
  }, []);

  const handleEdit = (limit: TierLimit) => {
    setEditingTier(limit.tier);
    setEditState({
      dailyTransactionLimit: limit.dailyTransactionLimit.toString(),
      monthlyTransactionLimit: String(limit.monthlyTransactionLimit ?? 0),
      balanceLimit: limit.balanceLimit.toString(),
      singleTransactionLimit: limit.singleTransactionLimit.toString(),
      usdDailyTransactionLimit: String(limit.usdDailyTransactionLimit ?? 0),
      usdMonthlyTransactionLimit: String(limit.usdMonthlyTransactionLimit ?? 0),
      usdSingleTransactionLimit: String(limit.usdSingleTransactionLimit ?? 0),
      ibraCharsPerDay: String(limit.ibraCharsPerDay ?? CHAT_DEFAULTS[limit.tier] ?? 0),
      voiceCharsPerDay: String(limit.voiceCharsPerDay ?? VOICE_DEFAULTS[limit.tier] ?? 0),
    });
  };

  const handleSave = (limit: TierLimit) => {
    const daily = parseNonNegative(editState.dailyTransactionLimit);
    const monthly = parseNonNegative(editState.monthlyTransactionLimit);
    const balance = parseNonNegative(editState.balanceLimit);
    const single = parseNonNegative(editState.singleTransactionLimit);
    const usdDaily = parseNonNegative(editState.usdDailyTransactionLimit);
    const usdMonthly = parseNonNegative(editState.usdMonthlyTransactionLimit);
    const usdSingle = parseNonNegative(editState.usdSingleTransactionLimit);
    const chat = parseInt(editState.ibraCharsPerDay);
    const voice = parseInt(editState.voiceCharsPerDay);
    if (
      daily === null || monthly === null || balance === null || single === null ||
      usdDaily === null || usdMonthly === null || usdSingle === null ||
      isNaN(chat) || chat < 0 || isNaN(voice) || voice < 0
    ) return;
    setPendingUpdate({
      tier: limit.tier,
      dailyTransactionLimit: daily,
      monthlyTransactionLimit: monthly,
      balanceLimit: balance,
      singleTransactionLimit: single,
      usdDailyTransactionLimit: usdDaily,
      usdMonthlyTransactionLimit: usdMonthly,
      usdSingleTransactionLimit: usdSingle,
      ibraCharsPerDay: chat,
      voiceCharsPerDay: voice,
    });
    setEditingTier(null);
  };

  const executeUpdate = async () => {
  if (!pendingUpdate) return;
  try {
    setActionLoading(true);
    const { tier, ...updates } = pendingUpdate;
    await configApi.updateTierLimit(tier, updates);
    await fetchLimits();
    setPendingUpdate(null);
    toast.success(`${tierLabel(pendingUpdate.tier)} limits updated successfully.`);
  } catch (err) {
    toast.error(err, 'Failed to update tier limit.');
  } finally {
    setActionLoading(false);
  }
};

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={24} />
      </div>
    );
  }

  const inputStyle: React.CSSProperties = {
    width: 120,
    height: 34,
    padding: '0 8px',
    fontSize: 13,
    fontFamily: 'var(--font-family)',
    color: 'var(--neutral-800)',
    background: 'var(--neutral-100)',
    border: '1px solid var(--blue-600)',
    borderRadius: 'var(--radius-md)',
    outline: 'none',
  };

  return (
    <>
      <Card padding={0}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--neutral-300)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>
            Tier Limits
          </h3>
          <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
            Transaction, account balance, card spending, and AI prompt quotas per KYC tier
          </p>
        </div>

        {error && (
          <div
            style={{
              margin: 20,
              padding: 12,
              background: 'var(--danger-100)',
              color: 'var(--danger-900)',
              fontSize: 13,
              borderRadius: 'var(--radius-md)',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--neutral-200)' }}>
                {[
                  'Tier',
                  'Daily Limit',
                  'Monthly Limit',
                  'Balance Limit',
                  'Single Txn Limit',
                  'USD Card Limits',
                  'Ibra chat chars/Day',
                  'Voice chars/Day',
                  '',
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--neutral-600)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      borderBottom: '1px solid var(--neutral-300)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {limits.map((limit, i) => (
                <tr
                  key={limit.tier}
                  style={{
                    background: i % 2 === 0 ? 'var(--neutral-100)' : 'var(--neutral-200)',
                    borderBottom: '1px solid var(--neutral-300)',
                  }}
                >
                  <td
                    style={{
                      padding: '14px 16px',
                      fontWeight: 600,
                      color: 'var(--neutral-800)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tierLabel(limit.tier)}
                  </td>

                  {editingTier === limit.tier ? (
                    <>
                      <td style={{ padding: '10px 16px' }}>
                        <input
                          type="number"
                          value={editState.dailyTransactionLimit}
                          onChange={(e) =>
                            setEditState((s) => ({
                              ...s,
                              dailyTransactionLimit: e.target.value,
                            }))
                          }
                          style={inputStyle}
                        />
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <input
                          type="number"
                          value={editState.monthlyTransactionLimit}
                          onChange={(e) =>
                            setEditState((s) => ({
                              ...s,
                              monthlyTransactionLimit: e.target.value,
                            }))
                          }
                          style={inputStyle}
                        />
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <input
                          type="number"
                          value={editState.balanceLimit}
                          onChange={(e) =>
                            setEditState((s) => ({ ...s, balanceLimit: e.target.value }))
                          }
                          style={inputStyle}
                        />
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <input
                          type="number"
                          value={editState.singleTransactionLimit}
                          onChange={(e) =>
                            setEditState((s) => ({
                              ...s,
                              singleTransactionLimit: e.target.value,
                            }))
                          }
                          style={inputStyle}
                        />
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ display: 'grid', gap: 6, minWidth: 150 }}>
                          <LimitInputRow
                            label="Single"
                            value={editState.usdSingleTransactionLimit}
                            onChange={(value) =>
                              setEditState((s) => ({
                                ...s,
                                usdSingleTransactionLimit: value,
                              }))
                            }
                          />
                          <LimitInputRow
                            label="Daily"
                            value={editState.usdDailyTransactionLimit}
                            onChange={(value) =>
                              setEditState((s) => ({
                                ...s,
                                usdDailyTransactionLimit: value,
                              }))
                            }
                          />
                          <LimitInputRow
                            label="Monthly"
                            value={editState.usdMonthlyTransactionLimit}
                            onChange={(value) =>
                              setEditState((s) => ({
                                ...s,
                                usdMonthlyTransactionLimit: value,
                              }))
                            }
                          />
                        </div>
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <input
                          type="number"
                          aria-label="Ibra chat characters per day"
                          value={editState.ibraCharsPerDay}
                          onChange={(e) =>
                            setEditState((s) => ({
                              ...s,
                              ibraCharsPerDay: e.target.value,
                            }))
                          }
                          style={inputStyle}
                        />
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <input
                          type="number"
                          aria-label="Voice characters per day"
                          value={editState.voiceCharsPerDay}
                          onChange={(e) =>
                            setEditState((s) => ({
                              ...s,
                              voiceCharsPerDay: e.target.value,
                            }))
                          }
                          style={inputStyle}
                        />
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
                          <Button size="sm" onClick={() => handleSave(limit)}>
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingTier(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td
                        style={{
                          padding: '14px 16px',
                          fontFamily: 'monospace',
                          color: 'var(--neutral-700)',
                        }}
                      >
                        {formatNaira(limit.dailyTransactionLimit)}
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          fontFamily: 'monospace',
                          color: 'var(--neutral-700)',
                        }}
                      >
                        {formatNaira(limit.monthlyTransactionLimit)}
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          fontFamily: 'monospace',
                          color: 'var(--neutral-700)',
                        }}
                      >
                        {formatNaira(limit.balanceLimit)}
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          fontFamily: 'monospace',
                          color: 'var(--neutral-700)',
                        }}
                      >
                        {formatNaira(limit.singleTransactionLimit)}
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          fontFamily: 'monospace',
                          color: 'var(--neutral-700)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <div style={{ display: 'grid', gap: 4 }}>
                          <span>Single: {formatUsd(limit.usdSingleTransactionLimit)}</span>
                          <span>Daily: {formatUsd(limit.usdDailyTransactionLimit)}</span>
                          <span>Monthly: {formatUsd(limit.usdMonthlyTransactionLimit)}</span>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          color: 'var(--neutral-700)',
                          fontWeight: 500,
                        }}
                        title="What is asked of Ibra plus what Ibra answers. The app shows it as a share of the day"
                      >
                        {(limit.ibraCharsPerDay ?? CHAT_DEFAULTS[limit.tier] ?? 0).toLocaleString('en-NG')}
                      </td>
                      <td
                        style={{
                          padding: '14px 16px',
                          color: 'var(--neutral-700)',
                          fontWeight: 500,
                        }}
                        title="What is said to Ibra, once transcribed, plus what Ibra says back"
                      >
                        {(limit.voiceCharsPerDay ?? VOICE_DEFAULTS[limit.tier] ?? 0).toLocaleString('en-NG')}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {canEdit && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleEdit(limit)}
                          >
                            Edit
                          </Button>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ConfirmDialog
        isOpen={pendingUpdate !== null}
        title={`Update ${pendingUpdate ? tierLabel(pendingUpdate.tier) : ''} limits?`}
        message={`Daily: ${pendingUpdate ? formatNaira(pendingUpdate.dailyTransactionLimit) : ''} — Monthly: ${pendingUpdate ? formatNaira(pendingUpdate.monthlyTransactionLimit) : ''} — Balance: ${pendingUpdate ? formatNaira(pendingUpdate.balanceLimit) : ''} — Single Txn: ${pendingUpdate ? formatNaira(pendingUpdate.singleTransactionLimit) : ''} — USD card monthly: ${pendingUpdate ? formatUsd(pendingUpdate.usdMonthlyTransactionLimit) : ''} — Ibra chat: ${pendingUpdate ? pendingUpdate.ibraCharsPerDay.toLocaleString('en-NG') : ''} characters/day — Voice: ${pendingUpdate ? pendingUpdate.voiceCharsPerDay.toLocaleString('en-NG') : ''} characters/day. Takes effect immediately.`}
        confirmLabel="Update Limits"
        variant="primary"
        loading={actionLoading}
        onConfirm={executeUpdate}
        onCancel={() => setPendingUpdate(null)}
      />
    </>
  );
};
